import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { getAssetUrl } from '../lib/supabaseClient';

export const ScrollPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Estado para saber si la animación ya terminó y podemos liberar el scroll normal
  const [isUnlocked, setIsUnlocked] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  // Estado para controlar el progreso de la animación (0 a 1)
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMobile, setIsMobile] = useState(() => 
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  );
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoUrl = getAssetUrl(isMobile ? 'intro-video-Mobile.mp4' : 'intro-video.mp4', 'video', false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      const v = videoRef.current;
      
      const handleLoaded = () => {
        if (v.duration && !isNaN(v.duration)) {
          setDuration(v.duration);
          // Hack para iOS: usar 0.01 en lugar de 0 evita la pantalla negra inicial
          if (v.currentTime === 0) {
            v.currentTime = 0.01;
          }
        }
      };

      v.addEventListener('loadedmetadata', handleLoaded);
      v.addEventListener('loadeddata', handleLoaded);
      
      // Hack para iOS: Forzar a que el navegador decodifique el video
      v.load();
      const playPromise = v.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          v.pause();
        }).catch(() => {
          // Silently handle if browser blocks autoplay
        });
      }

      return () => {
        v.removeEventListener('loadedmetadata', handleLoaded);
        v.removeEventListener('loadeddata', handleLoaded);
      };
    }
  }, [videoUrl]);

  useEffect(() => {
    const handleSkip = () => {
      setIsUnlocked(true);
      setProgress(1.3); // Lo mandamos al final del buffer
      document.body.style.overflow = 'auto';
    };

    window.addEventListener('skip-intro', handleSkip);
    
    // Si el usuario entra directamente con un hash (ej: /#paddock), saltamos la intro
    if (typeof window !== 'undefined') {
      const hash = window.location.hash;
      if (hash && hash !== '#inicio' && hash !== '#') {
        handleSkip();
      } else {
        window.scrollTo(0, 0);
      }
    }

    return () => window.removeEventListener('skip-intro', handleSkip);
  }, []);

  useEffect(() => {
    // Si ya se desbloqueó, no hacemos nada más
    if (isUnlocked) return;

    // Bloqueamos el scroll del body mientras la animación no termine
    document.body.style.overflow = 'hidden';

    const handleWheel = (e: WheelEvent) => {
      // Prevenimos el scroll real de la página
      e.preventDefault();

      // Ajustamos la sensibilidad del scroll (más bajo = más scroll necesario)
      // Aumentado a 0.0007 para requerir menos scroll en PC
      const sensitivity = 0.0007; 
      
      setProgress((prev) => {
        // Calculamos el nuevo progreso basado en la rueda del mouse
        let newProgress = prev + (e.deltaY * sensitivity);
        
        // Limitamos entre 0 y 1.2 (0 a 1 es video, 1 a 1.2 es "buffer" en el Hero)
        newProgress = Math.max(0, Math.min(1.2, newProgress));

        // Desbloqueamos la página solo cuando superamos el buffer (1.1)
        if (newProgress >= 1.2) {
          setIsUnlocked(true);
          document.body.style.overflow = 'auto';
        }

        return newProgress;
      });
    };

    // Usamos { passive: false } para poder usar e.preventDefault()
    window.addEventListener('wheel', handleWheel, { passive: false });
    
    // Soporte para pantallas táctiles (móviles)
    let touchStartY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const touchEndY = e.touches[0].clientY;
      const deltaY = touchStartY - touchEndY;
      
      // Aumentado a 0.0015 para requerir menos arrastre en móviles
      const sensitivity = 0.0015;
      
      setProgress((prev) => {
        let newProgress = prev + (deltaY * sensitivity);
        newProgress = Math.max(0, Math.min(1.3, newProgress));
        if (newProgress >= 1.3) {
          setIsUnlocked(true);
          document.body.style.overflow = 'auto';
        }
        return newProgress;
      });
      
      touchStartY = touchEndY; // Reset for continuous swiping
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      document.body.style.overflow = 'auto';
    };
  }, [isUnlocked]);

  // Cálculos matemáticos basados en el progreso (0 a 1)
  
  // Sincronizar el progreso del scroll con el tiempo del video
  const targetTimeRef = useRef(0.01);
  const lastUpdateRef = useRef<number>(0);

  // Actualizamos el tiempo objetivo cada vez que el usuario hace scroll
  useEffect(() => {
    const clampedProgress = Math.min(1, progress);
    const calculatedTime = clampedProgress >= 0.99 ? duration : clampedProgress * duration;
    // Hack para iOS: nunca bajar de 0.01s para evitar pantalla negra
    targetTimeRef.current = Math.max(0.01, calculatedTime);
  }, [progress, duration]);

  // Bucle de renderizado continuo para suavizar el movimiento (Lerp)
  useEffect(() => {
    if (!videoRef.current || !duration || isUnlocked) return;

    let animationFrameId: number;
    const v = videoRef.current;

    const renderLoop = (timestamp: number) => {
      // Limitamos el framerate del video scrub a ~30fps en móviles para no ahogar el decodificador de hardware
      if (isMobile && timestamp - lastUpdateRef.current < 30) {
        animationFrameId = requestAnimationFrame(renderLoop);
        return;
      }
      lastUpdateRef.current = timestamp;

      const targetTime = targetTimeRef.current;
      const diff = targetTime - v.currentTime;
      
      // En dispositivos móviles (touch), la entrada ya es suave por arrastre.
      // Hacemos que sea directo (1.0) para tener 1-a-1 con el dedo y evitar lag.
      // En desktop, aplicamos un poco de inercia para los saltos de rueda de mouse.
      const lerpFactor = isMobile ? 1.0 : 0.08;
      
      // Si hay una diferencia, movemos el video
      if (!v.seeking && Math.abs(diff) > 0.03) {
        v.currentTime += diff * lerpFactor;
      } else if (!v.seeking && Math.abs(diff) > 0.001) {
        v.currentTime = targetTime;
      }

      animationFrameId = requestAnimationFrame(renderLoop);
    };

    animationFrameId = requestAnimationFrame(renderLoop);

    return () => cancelAnimationFrame(animationFrameId);
  }, [duration, isMobile, isUnlocked]);

  // Opacidad del HUD: desaparece en el primer 30%
  const hudOpacity = progress < 0.3 ? 1 - (progress / 0.3) : 0;
  
  // Opacidad de la capa del video: desaparece durante el buffer (de 1.1 a 1.2)
  const overlayOpacity = progress < 1.0 ? 1 : Math.max(0, 1 - ((progress - 1.05) * 10));

  // Opacidad del contenido del Hero: aparece exactamente durante el buffer (de 1.0 a 1.1)
  const contentOpacity = isUnlocked ? 1 : (progress > 1.0 ? Math.min(1, (progress - 1.05) * 10) : 0);

  return (
    <>
      {/* 
        1. EL CONTENIDO REAL (Hero)
        Lo envolvemos en un div de transición para que aparezca suavemente
      */}
      <div 
        style={{ 
          opacity: contentOpacity,
          transition: isUnlocked ? 'opacity 0.8s ease-out' : 'none'
        }}
      >
        {children}
      </div>

      {/* 
        2. LA CAPA DEL PORTAL (VIDEO)
        Solo existe mientras no se haya desbloqueado (isUnlocked === false)
        o mientras se esté desvaneciendo.
      */}
      {!isUnlocked && (
        <div 
          className="fixed inset-0 z-[100] bg-[#050505] flex items-center justify-center overflow-hidden"
          style={{ 
            opacity: overlayOpacity,
            // Evitamos que intercepte clics si ya casi no se ve
            pointerEvents: progress > 1.0 ? 'none' : 'auto' 
          }}
        >
          {/* VIDEO PORTAL - Alineación corregida: top-0 y h-[100vh] para calzar con el Hero */}
          <video
            ref={videoRef}
            className="absolute top-0 left-0 w-[100vw] h-[100vh] object-cover object-center opacity-70 pointer-events-none"
            playsInline
            muted
            autoPlay
            preload="auto"
            controls={false}
            disablePictureInPicture
            disableRemotePlayback
            src={videoUrl} 
          />

          {/* CAPA INVISIBLE PARA BLOQUEAR INTERACCIÓN DIRECTA CON EL VIDEO (Evita el botón de Play en móviles) */}
          <div className="absolute inset-0 z-10 bg-transparent" />

          {/* Gradientes de coincidencia con el Hero */}
          <div className="absolute inset-0 z-20 pointer-events-none" style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0.7) 100%)" }} />
          <div className="absolute inset-0 z-20 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.4) 100%)" }} />

          {/* HUD FPV */}
          <div 
            className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none transition-opacity duration-300"
            style={{ opacity: hudOpacity }}
          >
             {/* Visor Frame */}
             <div className="w-[300px] h-[140px] md:w-[600px] md:h-[280px] flex flex-col justify-between p-4 md:p-8 relative">
                <div className="absolute inset-0 border-2 border-cyan-400/30 rounded-[40px] md:rounded-[80px]" />
                
                <div className="flex justify-between text-cyan-400 font-racing text-[10px] md:text-sm tracking-widest">
                  <span className="flex items-center gap-2">
                    <div className="w-2 h-2 md:w-3 md:h-3 bg-red-500 rounded-full animate-pulse" /> REC
                  </span>
                  <span>BAT 98%</span>
                </div>
                
                {/* Crosshair */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center opacity-50">
                  <div className="w-12 h-px bg-cyan-400 absolute" />
                  <div className="h-12 w-px bg-cyan-400 absolute" />
                  <div className="w-16 h-16 border border-cyan-400 rounded-full absolute" />
                </div>

                <div className="flex justify-center">
                  <span className="text-cyan-400 font-racing text-[10px] md:text-sm tracking-[0.5em] animate-pulse">
                    LINKING...
                  </span>
                </div>
             </div>
            
            {/* Scroll Instructions */}
            <div className="absolute bottom-12 left-1/2 -translate-x-1/2 text-cyan-400 font-racing text-[10px] md:text-xs tracking-[0.3em] flex flex-col items-center gap-3">
              <span className="uppercase">Iniciando Enlace FPV</span>
              <span className="text-[8px] text-gray-400 tracking-widest">SCROLL PARA ENTRAR</span>
              <div className="w-px h-12 bg-gradient-to-b from-cyan-400 to-transparent animate-pulse" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
